/**
 * Helper function to get translated text with fallback for Japanese translations
 * @param {Function} t - Translation function from useTranslation
 * @param {string} key - Translation key (e.g., 'reportIssue.description')
 * @param {boolean} isLanguageChanged - Whether language is changed to Japanese
 * @param {Object} japaneseTranslations - Fallback Japanese translations object
 * @param {string} defaultValue - Default value if translation not found
 * @returns {string} - Translated text or fallback
 */
export const getTranslation = (t, key, isLanguageChanged, japaneseTranslations, defaultValue = '') => {
    // Check if Japanese language is selected (from Redux or localStorage)
    const isJapanese = isLanguageChanged || localStorage.getItem('languageISOCode') === 'ja';
    
    // Helper to get Japanese fallback value
    const getJapaneseFallback = () => {
        const keys = key.split('.');
        let value = japaneseTranslations;
        for (const k of keys) {
            value = value?.[k];
            if (!value) break;
        }
        return value || defaultValue;
    };
    
    // If Japanese is selected, prioritize Japanese fallback
    if (isJapanese) {
        const japaneseFallback = getJapaneseFallback();
        if (japaneseFallback) {
            return japaneseFallback;
        }
    }
    
    try {
        const translated = t(key); 
        if (translated && translated !== key) {
            return translated;
        }
        // If translation failed and Japanese is selected, use Japanese fallback
        if (isJapanese) {
            return getJapaneseFallback();
        }
        return defaultValue;
    } catch (error) {
        // If there's any error, return fallback for Japanese or default
        if (isJapanese) {
            return getJapaneseFallback();
        }
        return defaultValue;
    }
};

export const getDeviceType = () => {
    const userAgent = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
        return 'Tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/i.test(userAgent)) {
        return 'Mobile';
    }
    return 'Desktop';
};

export const getBrowserType = () => {
    const userAgent = navigator.userAgent;
    
    // Check for specific browsers in order of specificity
    if (userAgent.indexOf('Edg') !== -1) {
        // Edge (Chromium-based)
        const match = userAgent.match(/Edg\/([0-9.]+)/);
        return match ? `Edge/${match[1]}` : 'Edge';
    } else if (userAgent.indexOf('Chrome') !== -1 && userAgent.indexOf('Edg') === -1) {
        // Chrome (must check before Safari since Chrome also contains Safari)
        const match = userAgent.match(/Chrome\/([0-9.]+)/);
        return match ? `Chrome/${match[1]}` : 'Chrome';
    } else if (userAgent.indexOf('Safari') !== -1 && userAgent.indexOf('Chrome') === -1) {
        // Safari (only if Chrome is not present)
        const match = userAgent.match(/Version\/([0-9.]+)/);
        return match ? `Safari/${match[1]}` : 'Safari';
    } else if (userAgent.indexOf('Firefox') !== -1) {
        // Firefox
        const match = userAgent.match(/Firefox\/([0-9.]+)/);
        return match ? `Firefox/${match[1]}` : 'Firefox';
    } else if (userAgent.indexOf('MSIE') !== -1 || userAgent.indexOf('Trident') !== -1) {
        // Internet Explorer
        const match = userAgent.match(/(MSIE |rv:)([0-9.]+)/);
        return match ? `IE/${match[2]}` : 'IE';
    }
    
    // Fallback: Extract any Browser/Version pattern from user agent
    // Looks for patterns like "BrowserName/Version" (e.g., "Opera/12.0", "Brave/1.2.3")
    const browserPattern = /([A-Za-z]+)\/([0-9.]+)/g;
    const matches = [...userAgent.matchAll(browserPattern)];
    
    if (matches.length > 0) {
        // Filter out common non-browser identifiers
        const excluded = ['Mozilla', 'AppleWebKit', 'KHTML', 'Gecko', 'like', 'Mobile'];
        const validBrowsers = matches.filter(match => !excluded.includes(match[1]));
        
        if (validBrowsers.length > 0) {
            // Return the last valid browser found (usually the most specific)
            const lastBrowser = validBrowsers[validBrowsers.length - 1];
            return `${lastBrowser[1]}/${lastBrowser[2]}`;
        }
    }
    
    // Final fallback: return "Unknown"
    return 'Unknown';
};

// Convert file to base64
export const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });
};
